import { useTranslate } from '@refinedev/core';
import { Avatar, Card, Flex, Grid, Tabs, Tag, Typography, theme } from 'antd';
import { StatCell } from '@/components/common/show/StatCell';
import type { ProfileOut, UserOut } from '@/api/generated/schemas';
import { ShowLink } from '@/components/common/ShowLink';
import { ProfileRoleTag } from '@/components/iam/tags';
import { PROFILE_SHOW_TABS } from '../constants';
import type { ProfileShowTab } from '../constants';

const { Text, Title } = Typography;

const TAB_LABEL_KEYS: Record<ProfileShowTab, string> = {
  profile: 'account.profiles.tabs.profile',
  favorites: 'account.profiles.tabs.favorites',
  collections: 'account.profiles.tabs.collections',
  progressions: 'account.profiles.tabs.progressions',
  sessions: 'account.profiles.tabs.sessions',
  cycles: 'account.profiles.tabs.cycles',
  reviews: 'account.profiles.tabs.reviews',
};

interface ProfileShowHeaderProps {
  record: ProfileOut;
  user?: UserOut;
  activeTab: ProfileShowTab;
  onTabChange: (key: string) => void;
}

export const ProfileShowHeader = ({
  record,
  user,
  activeTab,
  onTabChange,
}: ProfileShowHeaderProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  const favoritesCount =
    (record.favorite_category_ids?.length ?? 0) +
    (record.favorite_collection_ids?.length ?? 0);

  return (
    <Card styles={{ body: { paddingBottom: 0 } }}>
      <Flex gap={token.marginLG} align="flex-start" vertical={!screens.sm}>
        <Avatar
          size={64}
          src={record.avatar_url ?? undefined}
          style={
            record.avatar_url
              ? undefined
              : {
                  backgroundColor: token.colorPrimary,
                  fontSize: token.fontSizeHeading4,
                }
          }
        >
          {record.name?.[0]?.toUpperCase() ?? '#'}
        </Avatar>

        <div style={{ flex: 1, minWidth: 0 }}>
          <Flex gap={token.marginSM} align="baseline" wrap>
            {/* Long names wrap instead of pushing the tabs bar out. */}
            <Title
              level={3}
              style={{
                margin: 0,
                fontSize: token.fontSizeHeading3,
                overflowWrap: 'anywhere',
              }}
            >
              {record.name ?? `#${record.id}`}
            </Title>
            <ProfileRoleTag role={record.role} />
            {record.deleted_at && <Tag color="error">{t('base.deleted')}</Tag>}
          </Flex>

          <Text
            type="secondary"
            style={{ display: 'block', marginTop: token.marginXXS }}
          >
            {`ID ${record.id}`}
          </Text>

          <Flex
            gap={token.marginXL + token.margin}
            style={{ marginTop: token.marginLG }}
            wrap
          >
            <StatCell label={t('account.profiles.fields.user')}>
              <ShowLink resource="iam.users" id={record.user_id}>
                {user?.email ?? user?.username ?? record.user_id}
              </ShowLink>
            </StatCell>
            <StatCell label={t('account.profiles.tabs.favorites')}>
              {favoritesCount}
            </StatCell>
            <StatCell label={t('account.profiles.tabs.collections')}>
              {record.collections?.length ?? 0}
            </StatCell>
          </Flex>
        </div>
      </Flex>

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        style={{ marginTop: token.marginSM }}
        tabBarStyle={{ marginBottom: 0 }}
        items={PROFILE_SHOW_TABS.map((key) => ({
          key,
          label: t(TAB_LABEL_KEYS[key]),
        }))}
      />
    </Card>
  );
};
